import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Loader2, Search, Wallet, Ban, AlertTriangle, CreditCard, TrendingDown,
  TrendingUp, Clock, Building2,
} from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

function money(v) {
  const n = Number(v || 0);
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const FILTERS = [
  { key: null,                 label: 'All' },
  { key: 'eligible',           label: 'Eligible' },
  { key: 'blocked',            label: 'Blocked' },
  { key: 'missing_w9',         label: 'Missing W-9' },
  { key: 'no_payment_method',  label: 'No Payment Method' },
  { key: 'ach_available',      label: 'ACH Available' },
  { key: 'check_available',    label: 'Check Available' },
  { key: 'positive_balance',   label: 'Positive Balance' },
  { key: 'negative_balance',   label: 'Negative Balance' },
  { key: 'agent',              label: 'Agent' },
  { key: 'agency',             label: 'Agency' },
  { key: 'subagency',          label: 'Subagency' },
];

export default function PaymentCenterPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingRows, setLoadingRows] = useState(true);
  const [filter, setFilter] = useState(null);
  const [q, setQ] = useState('');

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const { data } = await accountingAPI.paymentCenterSummary();
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load summary');
    } finally { setLoadingSummary(false); }
  }, []);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const params = { limit: 500 };
      if (filter) params.filter = filter;
      if (q.trim()) params.q = q.trim();
      const { data } = await accountingAPI.paymentCenterRecipients(params);
      setRows(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load recipients');
    } finally { setLoadingRows(false); }
  }, [filter, q]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const openRecipient = (r) => {
    if (r.recipient_type === 'agent') {
      navigate(`/agents/${r.recipient_id}?tab=payment-info`);
    } else {
      navigate(`/agencies/${r.recipient_id}?tab=payment-info`);
    }
  };

  return (
    <div className="page-content" data-testid="payment-center-page">
      <PageHeader
        title="Payment Center"
        description="Operational overview of who is ready to pay, who is blocked, and why."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Accounting', href: '/accounting/carrier-imports' },
          { label: 'Payment Center' },
        ]}
      />

      {/* Summary strip */}
      {loadingSummary || !summary ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <SummaryCard testid="pc-ready"    label="Ready to Pay"          value={money(summary.ready_to_pay)}                icon={Wallet}          tone="emerald" />
          <SummaryCard testid="pc-blocked"  label="Blocked Payments"      value={summary.blocked_payments}                    icon={Ban}             tone="red" />
          <SummaryCard testid="pc-w9"       label="Missing W-9"           value={summary.missing_w9}                          icon={AlertTriangle}   tone="amber" />
          <SummaryCard testid="pc-method"   label="No Valid Payment Method" value={summary.no_valid_payment_method}           icon={CreditCard}      tone="amber" />
          <SummaryCard testid="pc-recovery" label="Outstanding Recovery"  value={money(summary.outstanding_recovery)}         icon={TrendingDown}    tone="red"   subtitle="To recover from future payments" />
          <SummaryCard testid="pc-payables" label="Outstanding Positive Payables" value={money(summary.outstanding_positive_payables)} icon={TrendingUp} tone="slate" />
          <SummaryCard testid="pc-approval" label="Pending Approval"      value={summary.pending_approval}                    icon={Clock}           tone="slate" />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search recipient name / ID…"
            className="pl-8 h-9"
            data-testid="pc-search"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <Badge key={f.key || 'all'}
                   variant={filter === f.key ? 'default' : 'outline'}
                   className="cursor-pointer text-xs"
                   onClick={() => setFilter(f.key)}
                   data-testid={`pc-filter-${f.key || 'all'}`}>{f.label}</Badge>
          ))}
        </div>
        <div className="ml-auto text-xs text-slate-500">{rows.length} recipient(s)</div>
      </div>

      {/* Recipient table */}
      <Card>
        <CardContent className="p-0">
          {loadingRows ? (
            <div className="p-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="p-16 text-center text-slate-500">
              <Wallet className="mx-auto mb-2 text-slate-300" size={22} />
              <div>No recipients match this filter yet.</div>
              <div className="text-xs text-slate-400 mt-1">
                Generate statements or wait for reconciliation to populate this list.
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="pc-recipients-table">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                    <th className="py-2 px-3">Recipient</th>
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3 text-right">Payable</th>
                    <th className="py-2 px-3 text-right">Recovery</th>
                    <th className="py-2 px-3 text-right">Net Balance</th>
                    <th className="py-2 px-3">W-9</th>
                    <th className="py-2 px-3">Payment Method</th>
                    <th className="py-2 px-3">Eligibility</th>
                    <th className="py-2 px-3">Blocking Reason</th>
                    <th className="py-2 px-3">Last Statement</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.recipient_type}:${r.recipient_id}`}
                        onClick={() => openRecipient(r)}
                        className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer"
                        data-testid={`pc-row-${r.recipient_type}-${r.recipient_id}`}>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1.5">
                          <Building2 size={11} className="text-slate-400" />
                          <span className="font-medium">{r.recipient_name}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3 capitalize text-slate-600">{r.recipient_type}</td>
                      <td className="py-2 px-3 text-right font-mono">{money(r.outstanding_payable)}</td>
                      <td className="py-2 px-3 text-right font-mono text-red-700">{r.outstanding_recovery > 0 ? money(r.outstanding_recovery) : '—'}</td>
                      <td className={`py-2 px-3 text-right font-mono font-semibold ${r.net_balance < 0 ? 'text-red-700' : 'text-slate-800'}`}>
                        {money(r.net_balance)}
                      </td>
                      <td className="py-2 px-3">
                        <Badge className={`text-[10px] py-0 px-1.5 ${
                          r.w9_status === 'complete' ? 'bg-emerald-100 text-emerald-800' :
                          r.w9_status === 'rejected' ? 'bg-red-100 text-red-800' :
                          r.w9_status === 'pending_review' ? 'bg-amber-100 text-amber-900' :
                          'bg-slate-200 text-slate-700'
                        }`}>{r.w9_status.replace(/_/g, ' ')}</Badge>
                      </td>
                      <td className="py-2 px-3 text-xs">{r.payment_methods}</td>
                      <td className="py-2 px-3">
                        <Badge className={`text-[10px] py-0 px-1.5 gap-1 ${
                          r.eligibility === 'eligible' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                        }`}>{r.eligibility}</Badge>
                      </td>
                      <td className="py-2 px-3 text-[11px] text-slate-500">
                        {r.blocking_reason_labels?.[0] || '—'}
                      </td>
                      <td className="py-2 px-3 text-[11px] text-slate-500">
                        {r.last_statement_at ? r.last_statement_at.slice(0, 10) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, tone = 'slate', subtitle, upcoming, testid }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber:   'bg-amber-50 text-amber-900 border-amber-200',
    red:     'bg-red-50 text-red-800 border-red-200',
    slate:   'bg-slate-50 text-slate-800 border-slate-200',
  };
  return (
    <div className={`rounded-lg border ${tones[tone]} p-3 relative`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase font-medium opacity-70">{label}</div>
        <Icon size={13} className="opacity-70" />
      </div>
      <div className="text-2xl font-semibold font-mono">{value}</div>
      {subtitle && <div className="text-[10px] opacity-70 mt-0.5">{subtitle}</div>}
      {upcoming && <div className="absolute top-1 right-1 text-[9px] uppercase font-semibold text-slate-500 bg-white/70 rounded px-1">Upcoming</div>}
    </div>
  );
}
